<template>
  <div class="modal-mask" @mousedown.self.stop="closeModal">
    <div class="modal" @click.stop="closeTooltips">
      <div class="modal__head">
        <h3 class="modal__title">
          {{ type === 'update' ? `Редактировать задачу №${state.task_id}` : 'Добавить задачу' }}
        </h3>
        <div class="close" @click.stop="closeModal">
          <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 1L16 16" stroke="currentColor" stroke-width="2"/>
            <path d="M1 16L16 1" stroke="currentColor" stroke-width="2"/>
          </svg>
        </div>
      </div>
      <div class="main-info">
        <div class="page__row main-info__top">
          <div class="page__item">
            <AppInput flat
                      placeholder="Название задачи"
                      :value="state.name"
                      :valid="state.validation.name"
                      :width="mediaBreakpoint !== 'desktop' ? '100%' : '615px'"
                      @value-changed="setName"
                      @got-focus="clearValidation"
            />
          </div>
          <div class="important-check">
            <AppCheckbox :checked="Boolean(state.is_important)" @toggle-check="toggleImportance" title="Важная задача"/>
            <div class="important-check__icon">
              <svg width="17" height="22" viewBox="0 0 17 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15.7431 7.16331C12.9085 1.28629 4.59461 0 4.59461 0C4.59461 0 8.43092 4.89677 3.52834 8.76895C0.428173 11.2129 -0.797473 14.3887 0.530626 17.8573C1.54757 20.5141 3.69909 21.5964 6.01757 22C4.90956 19.556 5.76713 16.571 5.862 16.2516C8.0287 20.0839 12.123 16.2516 10.0664 12.0867C12.7605 12.7698 13.1627 18.2343 11.0947 21.6452C14.1493 20.523 16.1832 17.702 16.741 15.1871C17.3292 12.5569 16.8814 9.51411 15.7431 7.16331Z" fill="url(#paint0_linear_fire-icon)"/>
                <defs>
                  <linearGradient id="paint0_linear_fire-icon" x1="8.5" y1="0" x2="8.5" y2="22" gradientUnits="userSpaceOnUse">
                    <stop stop-color="#EA803A"/>
                    <stop offset="1" stop-color="#EA3A3A"/>
                  </linearGradient>
                </defs>
              </svg>
            </div>
          </div>
        </div>
        <div class="description">
          <div class="description__inner">
            <AppMessageInput not-message
                             :value="state.description"
                             textarea-height="154px"
                             @value-changed="setDescription"
                             @selection-range="setInputSelectionRange"
                             @got-focus="clearValidation"
                             @input-reference="setInputReference"
                             @paste-files="attachFiles"
            />
            <div class="description__attachment-preview" v-if="state.attachment.files.length !== 0 || state.attachment.tags.length !== 0">
              <AppMessageAttachmentPreview :files="state.attachment.files"
                                           :tags="state.attachment.tags"
                                           :editable="true"
                                           @remove-file="removeFile"
                                           @remove-tag="removeTag"
              />
            </div>
            <div class="description__attachment">
              <AppMessageAttachment :link-tooltip="state.attachment.linkTooltip"
                                    :tag-tooltip="state.attachment.tagTooltip"
                                    @toggle-link-tooltip="toggleLinkTooltip"
                                    @close-link-tooltip="closeLinkTooltip"
                                    @set-link-url="setLinkUrl"
                                    @toggle-tag-tooltip="toggleTagTooltip"
                                    @close-tag-tooltip="closeTagTooltip"
                                    @set-tag-text="setTagText"
                                    @attach-files="attachFiles"
                                    @attach-link="attachLink"
                                    @attach-tag="attachTag"
                                    @clear-validation="clearValidation"
              />
            </div>
            <transition name="scaleDown">
              <span v-if="state.validation.description.type" class="description__valid" :class="'description__valid_' + state.validation.description.type">
                  {{ state.validation.description.message }}
                  <img v-if=" state.validation.description.type === 'error' " src="/img/svg/error_icon.svg" alt="Ошибка" class="description__valid__icon">
                  <img v-if=" state.validation.description.type === 'warning' " src="/img/svg/warning_icon.svg" alt="Внимание" class="description__valid__icon">
                  <img v-if=" state.validation.description.type === 'success' " src="/img/svg/success_icon.svg" alt="Успешно" class="description__valid__icon">
              </span>
            </transition>
          </div>
        </div>
      </div>
      <div class="modal__body">
        <div class="page__row">
          <div class="page__item">
            <AppInputDate title="Крайний срок"
                          :width="mediaBreakpoint !== 'desktop' ? '100%' : '400px'"
                          :value="state.date_deadline"
                          type="single"
                          :timer="true"
                          :valid="{}"
                          @value-changed="setDateDeadline"
                          position="above center"
            />
          </div>
          <div class="page__item">
            <!-- по умолчанию ставится колонка (статус) Ждет выполнения -->
            <AppSelect title="Статус:"
                       placeholder="Выберите статус"
                       :width="mediaBreakpoint !== 'desktop' ? '100%' : '400px'"
                       :value="type === 'update' ? { id: state.column_id, name: state.column.name } : state.column"
                       :options="columnList"
                       @value-changed="setColumn"
            />
          </div>
        </div>
        <div class="page__row">
          <div class="page__item">
            <AppSelect title="Сделать подзадачей:"
                       placeholder="Выберите задачу"
                       :width="mediaBreakpoint !== 'desktop' ? '100%' : '400px'"
                       :value="state.parentTask"
                       :options="type === 'update' ? excludeCurrentTask(tasksList) : tasksList"
                       @value-changed="setParentTask"
            />
          </div>
        </div>
        <div class="page__row task-creator" v-if="false">
          <div class="task-creator__text">Постановщик:</div>
          <div class="user-avatar task-creator__avatar" v-if="type === 'update'">
            <img v-if="state.creator.avatar !== null" :src="state.creator.avatar.path"
                 :alt="state.creator.profile.surname" :title="state.creator.profile.surname">
            <img v-else src="/img/avatar-thumb.png"
                 :alt="state.creator.profile.surname" :title="state.creator.profile.surname">
          </div>
          <div class="user-avatar task-creator__avatar" v-else>
            <img v-if="currentUserData.avatar !== null" :src="currentUserData.avatar"
                 :alt="currentUserData.full_name" :title="currentUserData.full_name">
            <img v-else src="/img/avatar-thumb.png"
                 :alt="currentUserData.full_name" :title="currentUserData.full_name">
          </div>
        </div>
        <div class="page__row mb-40 align-start">
          <div class="page__item">
            <AppSelectMultiple title="Ответственные:"
                               placeholder="Назначьте ответственных"
                               :width="mediaBreakpoint !== 'desktop' ? '100%' : '400px'"
                               :value="state.assignees"
                               :options="users"
                               :is-users="true"
                               :valid="state.validation.assignees"
                               :allow-empty="type !== 'update'"
                               @open="clearValidation"
                               @add-item="addAssignee"
                               @remove-item="removeAssignee"
            />
          </div>
          <div class="page__item">
            <AppSelectMultiple title="Наблюдатели:"
                               placeholder="Выберите наблюдателей"
                               :width="mediaBreakpoint !== 'desktop' ? '100%' : '400px'"
                               :value="state.watchers"
                               :options="users"
                               :is-users="true"
                               @add-item="addWatcher"
                               @remove-item="removeWatcher"
            />
          </div>
        </div>
        <div class="modal__button-wrap">
          <div class="modal__button">
            <AppButton title="Сохранить"
                       type="third"
                       :disabled="state.isLoading"
                       @click.native="saveTask"/>
          </div>
          <div class="modal__button">
            <AppButton v-if="type === 'update'"
                       title="Отмена"
                       type="alt"
                       :disabled="state.isLoading"
                       @click.native="closeModal"/>
            <AppButton v-else title="Отмена"
                       type="alt"
                       :disabled="state.isLoading"
                       @click.native="clearAndCloseModal"/>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AppButton from '../page/button/AppButton.vue'
import AppInput from '../page/input/AppInput'
import AppMessageAttachment from '@/components/page/input/AppMessageAttachment'
import AppCheckbox from '@/components/page/input/AppCheckbox'
import AppInputDate from '@/components/page/input/AppInputDate'
import AppSelect from '@/components/page/input/AppSelect'
import AppSelectMultiple from '@/components/page/input/AppSelectMultiple'
import AppMessageAttachmentPreview from '@/components/page/input/AppMessageAttachmentPreview'
import AppMessageInput from '@/components/page/input/AppMessageInput'

export default {
  name: 'task-modal',
  components: {
    AppMessageInput,
    AppMessageAttachmentPreview,
    AppSelectMultiple,
    AppSelect,
    AppInputDate,
    AppCheckbox,
    AppMessageAttachment,
    AppInput,
    AppButton
  },
  props: {
    type: {
      type: String,
      default: 'create'
    },
    state: Object
  },
  data () {
    return {
      // ссылка на инпут описания, чтобы закидывать в него значение при редактировании, иначе оно не устанавливается
      messageInput: null
    }
  },
  computed: {
    users () {
      return this.$store.getters['[dictionaries] usersList']
    },
    currentUserData () {
      return this.$store.getters.currentUserData
    },
    tasksList () {
      return this.$store.getters['[dictionaries] tasksList']
    },
    columnList () {
      return this.$store.getters['[dictionaries] tasksColumnList']
    },
    mediaBreakpoint () {
      return this.$store.getters.getMediaBreakpoint
    }
  },
  methods: {
    loadOptions () {
      this.$store.dispatch('[dictionaries] getTaskColumns')
      this.$store.dispatch('[dictionaries] getTaskOptionList')
      this.$store.dispatch('[dictionaries] getUserList')
    },
    excludeCurrentTask (tasks) {
      return tasks.filter(task => {
        return task.id !== this.state.task_id
      })
    },
    setName (text) {
      this.$emit('set-name', text)
    },
    setDescription (text) {
      this.$emit('set-description', text)
    },
    toggleImportance () {
      this.$emit('toggle-importance')
    },
    toggleLinkTooltip () {
      this.$emit('toggle-link-tooltip')
    },
    closeLinkTooltip () {
      this.$emit('close-link-tooltip')
    },
    setInputSelectionRange (range) {
      this.$emit('selection-range', range)
    },
    setLinkUrl (url) {
      this.$emit('set-link-url', url)
    },
    toggleTagTooltip () {
      this.$emit('toggle-tag-tooltip')
    },
    closeTagTooltip () {
      this.$emit('close-tag-tooltip')
    },
    setTagText (text) {
      this.$emit('set-tag-text', text)
    },
    attachFiles (files) {
      this.$emit('attach-files', files)
    },
    removeFile (data) {
      this.$emit('remove-file', data)
    },
    attachLink () {
      this.$emit('attach-link')
    },
    attachTag () {
      this.$emit('attach-tag')
    },
    removeTag (index) {
      this.$emit('remove-tag', index)
    },
    clearValidation () {
      this.$emit('clear-validation')
    },
    setDateDeadline (date) {
      this.$emit('set-deadline', date)
    },
    setParentTask (task) {
      this.$emit('set-parent-task', task)
    },
    setColumn (column) {
      this.$emit('set-column', column)
    },
    addAssignee (user) {
      this.$emit('add-assignee', user)
    },
    removeAssignee (user) {
      this.$emit('remove-assignee', user)
    },
    addWatcher (user) {
      this.$emit('add-watcher', user)
    },
    removeWatcher (user) {
      this.$emit('remove-watcher', user)
    },
    saveTask () {
      this.$emit('save-task')
    },
    closeModal () {
      this.$emit('close-modal')
    },
    clearAndCloseModal () {
      this.$emit('clear-close-modal')
    },
    closeTooltips () {
      this.closeLinkTooltip()
      this.closeTagTooltip()
    },
    setInputReference (ref) {
      this.messageInput = ref
    }
  },
  mounted () {
    this.loadOptions()
    // закидываем описание при редактировании, иначе оно не устанавливается
    this.messageInput.innerHTML = this.state.description
  },
  created () {
    /* очищаем валидацию, иначе ошибки влияют на обе модалки (ред./содзать) */
    this.$store.dispatch('clearTaskListModalValidation')
    this.$store.dispatch('clearTaskInfoModalValidation')
  }
}
</script>

<style lang="scss" scoped>
@import '../../assets/css/system/_vars';
@import '../../assets/css/system/_mixins';

.modal-mask {
  align-items: flex-start;
  padding-top: 10px;
  padding-bottom: 100px;
  overflow: auto;

  @media screen and (max-width: $tablet_start) {
    padding-bottom: 0;
    padding-top: 0;
  }

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background-color: $color_third;
  }

  &::-webkit-scrollbar-thumb {
    background-color: $color_second-light;
  }
}

.modal {
  max-width: 898px;
  align-items: stretch;

  &__head {
    margin-bottom: 0;
  }

  &__body {
    padding: 0 42px 30px 24px;

    @media screen and (max-width: $tablet_start) {
      padding: 0 16px 20px 16px;

      .page__row {
        flex-direction: column;
      }

      .page__item {
        width: 100%;
        margin-right: 0;
        margin-bottom: 20px;
      }
    }
  }

  &__button-wrap {
    display: flex;
    flex-flow: row wrap;
    justify-content: flex-start;

    @media screen and (max-width: $tablet_start) {
      justify-content: center;
    }
  }

  &__button {
    margin-right: 16px;
    margin-bottom: 10px;

    &:last-child {
      margin-right: 0;
    }
  }
}

.main-info {
  background: $color_sixth;
  margin-bottom: 20px;

  &__top {
    padding: 20px 42px 20px 24px;
    margin-bottom: 0;

    @media screen and (max-width: $tablet_start) {
      padding: 16px 16px 20px;
      flex-direction: column;
      justify-content: flex-start;
      align-items: flex-start;

      .page__item {
        width: 100%;
        margin-right: 0;
        margin-bottom: 20px;
      }
    }
  }
}

.important-check {
  display: flex;
  flex-flow: row nowrap;

  &__icon {
    margin-left: 10px;
  }
}

.description {
  padding: 0 42px 20px 24px;

  @media screen and (max-width: $tablet_start) {
    padding: 0 16px 20px 16px;
  }

  &__inner {
    position: relative;
    padding: 16px 5px 24px;
    background: $color_main;
    border-radius: 3px;
  }

  &__attachment-preview {
    padding: 12px 15px 0;
  }

  &__attachment {
    margin-top: 20px;
    padding-right: 15px;
    padding-left: 15px;
  }

  &__valid {
    display: block;
    position: absolute;
    z-index: 99;
    top: 100%;
    bottom: auto;
    transform: translateY(-5px) scaleY(1);
    transform-origin: top;
    left: 0;
    width: 100%;
    font-size: 14px;
    padding: 5px 15px 0 15px;
    color: $font_color_alt;
    border-radius: 0 0 3px 3px;
    transition: .3s;

    &__icon {
      position: absolute;
      top: calc(50% - 12.5px);
      left: auto;
      right: -35px;
      animation: fadeIn .3s linear;
    }

    &_error {
      background-color: $color_error;
    }

    &_warning {
      background-color: $color_warning;
    }

    &_success {
      background-color: $color_success;
    }
  }
}

.task-creator {
  &__text {
    color: $font_color_second;
  }

  &__avatar {
    margin-left: 28px;
  }
}
</style>
